import { useState } from "react";
import axios from "axios";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Badge } from "../ui/badge";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Switch } from "../ui/switch";
import { Card, CardContent, CardHeader, CardTitle } from "../ui/card";
import { ImageUpload } from "../ui/image-upload";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "../ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "../ui/tabs";
import { toast } from "sonner";
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Bold,
  Italic,
  Heading2,
  Heading3,
  List,
  ListOrdered,
  Quote,
  Link2,
  Minus,
  ImageIcon,
  BookOpen,
  Search,
  Clock,
  BarChart3,
} from "lucide-react";
import { API } from "../../apiConfig";

const MARKDOWN_ACTIONS = [
  { icon: Bold, label: "Bold", before: "**", after: "**" },
  { icon: Italic, label: "Italic", before: "*", after: "*" },
  { icon: Heading2, label: "Heading 2", before: "## ", after: "" },
  { icon: Heading3, label: "Heading 3", before: "### ", after: "" },
  { icon: List, label: "Bullet List", before: "- ", after: "" },
  { icon: ListOrdered, label: "Numbered List", before: "1. ", after: "" },
  { icon: Quote, label: "Blockquote", before: "> ", after: "" },
  { icon: Link2, label: "Link", before: "[", after: "](url)" },
  { icon: Minus, label: "Divider", before: "\n---\n", after: "" },
  { icon: ImageIcon, label: "Image", before: "![alt](", after: ")" },
];

const estimateReadTime = (text) => {
  if (!text) return 0;
  return Math.max(1, Math.ceil(text.trim().split(/\s+/).length / 220));
};

const wordCount = (text) => {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(Boolean).length;
};

const ArticlesTab = ({ articles, token, onRefresh }) => {
  const [articleDialogOpen, setArticleDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [dialogTab, setDialogTab] = useState("content");
  const [articleForm, setArticleForm] = useState({
    title: "",
    slug: "",
    excerpt: "",
    content: "",
    author: "Art By Arawol",
    category: "news",
    featured_image: "",
    tags: "",
    meta_description: "",
    og_image: "",
    is_published: true,
    is_featured: false,
  });

  const headers = { Authorization: `Bearer ${token}` };

  const formatDate = (dateStr) => {
    if (!dateStr) return "—";
    return new Date(dateStr).toLocaleDateString("en-AU", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/(^-|-$)/g, "");
  };

  const openArticleDialog = (article = null) => {
    setDialogTab("content");
    if (article) {
      setEditingArticle(article);
      setArticleForm({
        title: article.title || "",
        slug: article.slug || "",
        excerpt: article.excerpt || "",
        content: article.content || "",
        author: article.author || "Art By Arawol",
        category: article.category || "news",
        featured_image: article.featured_image || "",
        tags: Array.isArray(article.tags)
          ? article.tags.join(", ")
          : article.tags || "",
        meta_description: article.meta_description || "",
        og_image: article.og_image || "",
        is_published: article.is_published ?? true,
        is_featured: article.is_featured ?? false,
      });
    } else {
      setEditingArticle(null);
      setArticleForm({
        title: "",
        slug: "",
        excerpt: "",
        content: "",
        author: "Art By Arawol",
        category: "news",
        featured_image: "",
        tags: "",
        meta_description: "",
        og_image: "",
        is_published: true,
        is_featured: false,
      });
    }
    setArticleDialogOpen(true);
  };

  const insertMarkdown = (before, after) => {
    const textarea = document.getElementById("article-content-editor");
    if (!textarea) return;
    const start = textarea.selectionStart;
    const end = textarea.selectionEnd;
    const selected = articleForm.content.substring(start, end);
    const newContent =
      articleForm.content.substring(0, start) +
      before +
      selected +
      after +
      articleForm.content.substring(end);
    setArticleForm({ ...articleForm, content: newContent });
    // Reset cursor after state update
    setTimeout(() => {
      textarea.focus();
      textarea.setSelectionRange(
        start + before.length,
        start + before.length + selected.length
      );
    }, 0);
  };

  const saveArticle = async () => {
    if (!articleForm.title.trim()) {
      toast.error("Title is required");
      return;
    }
    try {
      const articleData = {
        ...articleForm,
        slug: articleForm.slug || generateSlug(articleForm.title),
        tags: articleForm.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
        meta_description:
          articleForm.meta_description || articleForm.excerpt || "",
        og_image: articleForm.og_image || articleForm.featured_image || "",
      };

      if (editingArticle) {
        await axios.put(
          `${API}/admin/articles/${editingArticle.article_id}`,
          articleData,
          { headers }
        );
        toast.success("Article updated");
      } else {
        await axios.post(`${API}/admin/articles`, articleData, { headers });
        toast.success("Article created");
      }

      setArticleDialogOpen(false);
      onRefresh();
    } catch (error) {
      console.error("Error saving article:", error);
      toast.error("Failed to save article");
    }
  };

  const deleteArticle = async (articleId) => {
    if (!confirm("Are you sure you want to delete this article?")) return;
    try {
      await axios.delete(`${API}/admin/articles/${articleId}`, { headers });
      toast.success("Article deleted");
      onRefresh();
    } catch (error) {
      toast.error("Failed to delete article");
    }
  };

  const totalViews = articles.reduce((sum, a) => sum + (a.views || 0), 0);
  const publishedCount = articles.filter((a) => a.is_published).length;
  const draftCount = articles.filter((a) => !a.is_published).length;

  return (
    <>
      <Card className="rounded-2xl" data-testid="articles-tab">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5" />
              Articles & Journal
            </CardTitle>
            <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
              <span>{articles.length} articles</span>
              <span>{publishedCount} published</span>
              {draftCount > 0 && (
                <span className="text-amber-600">{draftCount} drafts</span>
              )}
              <span className="flex items-center gap-1">
                <BarChart3 className="h-3.5 w-3.5" />
                {totalViews} total views
              </span>
            </div>
          </div>
          <Button
            onClick={() => openArticleDialog()}
            className="rounded-full"
            data-testid="add-article-btn"
          >
            <Plus className="h-4 w-4 mr-2" /> New Article
          </Button>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Article</TableHead>
                <TableHead>Category</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Views</TableHead>
                <TableHead>Date</TableHead>
                <TableHead>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {articles.length === 0 ? (
                <TableRow>
                  <TableCell
                    colSpan={6}
                    className="text-center py-8 text-muted-foreground"
                  >
                    No articles yet. Click "New Article" to get started.
                  </TableCell>
                </TableRow>
              ) : (
                articles.map((article) => (
                  <TableRow key={article.article_id}>
                    <TableCell>
                      <div className="flex items-center gap-3">
                        {article.featured_image && (
                          <img
                            src={article.featured_image}
                            alt={article.title}
                            className="w-12 h-12 rounded object-cover"
                          />
                        )}
                        <div>
                          <p className="font-medium line-clamp-1">
                            {article.title}
                          </p>
                          <p className="text-xs text-muted-foreground line-clamp-1">
                            {article.excerpt}
                          </p>
                          <div className="flex items-center gap-2 mt-0.5">
                            {article.is_featured && (
                              <Badge variant="outline" className="text-[10px]">
                                Featured
                              </Badge>
                            )}
                            <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                              <Clock className="h-2.5 w-2.5" />
                              {estimateReadTime(article.content)} min read
                            </span>
                          </div>
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="capitalize">
                      {article.category?.replace("-", " ")}
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant={
                          article.is_published ? "default" : "secondary"
                        }
                      >
                        {article.is_published ? "Published" : "Draft"}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {article.views || 0}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {formatDate(article.created_at)}
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() =>
                            window.open(
                              `/articles/${article.slug}`,
                              "_blank"
                            )
                          }
                          title="Preview article"
                          data-testid={`preview-article-${article.article_id}`}
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openArticleDialog(article)}
                          data-testid={`edit-article-${article.article_id}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => deleteArticle(article.article_id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Article Editor Dialog */}
      <Dialog open={articleDialogOpen} onOpenChange={setArticleDialogOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingArticle ? "Edit Article" : "New Article"}
            </DialogTitle>
          </DialogHeader>

          <Tabs value={dialogTab} onValueChange={setDialogTab}>
            <TabsList className="grid grid-cols-3 mb-4">
              <TabsTrigger value="content">Content</TabsTrigger>
              <TabsTrigger value="media">Media & Tags</TabsTrigger>
              <TabsTrigger value="seo">SEO & Settings</TabsTrigger>
            </TabsList>

            {/* ── Content Tab ── */}
            <TabsContent value="content" className="space-y-4">
              <div className="space-y-2">
                <Label>Title</Label>
                <Input
                  value={articleForm.title}
                  onChange={(e) => {
                    setArticleForm({
                      ...articleForm,
                      title: e.target.value,
                      slug:
                        articleForm.slug || generateSlug(e.target.value),
                    });
                  }}
                  placeholder="Article title"
                  data-testid="article-title-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>URL Slug</Label>
                  <Input
                    value={articleForm.slug}
                    onChange={(e) =>
                      setArticleForm({ ...articleForm, slug: e.target.value })
                    }
                    placeholder="auto-generated-from-title"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Author</Label>
                  <Input
                    value={articleForm.author}
                    onChange={(e) =>
                      setArticleForm({
                        ...articleForm,
                        author: e.target.value,
                      })
                    }
                    placeholder="Author name"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Excerpt</Label>
                <Textarea
                  value={articleForm.excerpt}
                  onChange={(e) =>
                    setArticleForm({
                      ...articleForm,
                      excerpt: e.target.value,
                    })
                  }
                  placeholder="Brief summary — shown in article cards and search results"
                  rows={2}
                  data-testid="article-excerpt-input"
                />
              </div>

              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <Label>Content (Markdown)</Label>
                  <span className="text-xs text-muted-foreground">
                    {wordCount(articleForm.content)} words ·{" "}
                    {estimateReadTime(articleForm.content)} min read
                  </span>
                </div>
                {/* Markdown Toolbar */}
                <div className="flex items-center gap-0.5 p-1 border rounded-t-lg bg-muted/30 flex-wrap">
                  {MARKDOWN_ACTIONS.map((action) => (
                    <Button
                      key={action.label}
                      type="button"
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() =>
                        insertMarkdown(action.before, action.after)
                      }
                      title={action.label}
                    >
                      <action.icon className="h-4 w-4" />
                    </Button>
                  ))}
                </div>
                <Textarea
                  id="article-content-editor"
                  value={articleForm.content}
                  onChange={(e) =>
                    setArticleForm({
                      ...articleForm,
                      content: e.target.value,
                    })
                  }
                  placeholder="Write your article using Markdown…

## Heading
**Bold** and *italic* text
- Bullet points
> Blockquotes
[Link text](url)
---"
                  rows={16}
                  className="rounded-t-none font-mono text-sm"
                  data-testid="article-content-input"
                />
              </div>
            </TabsContent>

            {/* ── Media & Tags Tab ── */}
            <TabsContent value="media" className="space-y-4">
              <div className="space-y-2">
                <Label>Featured Image</Label>
                <ImageUpload
                  value={articleForm.featured_image}
                  onChange={(url) =>
                    setArticleForm({ ...articleForm, featured_image: url })
                  }
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Category</Label>
                  <Select
                    value={articleForm.category}
                    onValueChange={(v) =>
                      setArticleForm({ ...articleForm, category: v })
                    }
                  >
                    <SelectTrigger data-testid="article-category-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="news">News</SelectItem>
                      <SelectItem value="culture">Culture</SelectItem>
                      <SelectItem value="artist-spotlight">
                        Artist Spotlight
                      </SelectItem>
                      <SelectItem value="education">Education</SelectItem>
                      <SelectItem value="events">Events</SelectItem>
                      <SelectItem value="collecting">Collecting</SelectItem>
                      <SelectItem value="behind-the-scenes">
                        Behind the Scenes
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Tags (comma separated)</Label>
                <Input
                  value={articleForm.tags}
                  onChange={(e) =>
                    setArticleForm({ ...articleForm, tags: e.target.value })
                  }
                  placeholder="aboriginal art, culture, collecting, colin isaacs"
                  data-testid="article-tags-input"
                />
              </div>
            </TabsContent>

            {/* ── SEO & Settings Tab ── */}
            <TabsContent value="seo" className="space-y-4">
              <div className="space-y-2">
                <Label>Meta Description (SEO)</Label>
                <Textarea
                  value={articleForm.meta_description}
                  onChange={(e) =>
                    setArticleForm({
                      ...articleForm,
                      meta_description: e.target.value,
                    })
                  }
                  placeholder="Custom meta description for search engines. Falls back to excerpt if empty."
                  rows={3}
                  data-testid="article-meta-desc"
                />
                <p className="text-xs text-muted-foreground">
                  {(articleForm.meta_description || "").length}/160 characters
                  recommended
                </p>
              </div>

              <div className="space-y-2">
                <Label>Open Graph Image URL (social sharing)</Label>
                <Input
                  value={articleForm.og_image}
                  onChange={(e) =>
                    setArticleForm({ ...articleForm, og_image: e.target.value })
                  }
                  placeholder="Falls back to featured image if empty"
                  data-testid="article-og-image"
                />
              </div>

              <div className="flex gap-8 pt-4">
                <div className="flex items-center gap-2">
                  <Switch
                    checked={articleForm.is_published}
                    onCheckedChange={(v) =>
                      setArticleForm({ ...articleForm, is_published: v })
                    }
                    data-testid="article-published-switch"
                  />
                  <Label>Published</Label>
                </div>

                <div className="flex items-center gap-2">
                  <Switch
                    checked={articleForm.is_featured}
                    onCheckedChange={(v) =>
                      setArticleForm({ ...articleForm, is_featured: v })
                    }
                    data-testid="article-featured-switch"
                  />
                  <Label>Featured</Label>
                </div>
              </div>

              {editingArticle && (
                <div className="mt-4 p-4 rounded-xl bg-muted/40 text-sm text-muted-foreground space-y-1">
                  <p>
                    Views: <strong>{editingArticle.views || 0}</strong>
                  </p>
                  <p>Created: {formatDate(editingArticle.created_at)}</p>
                  <p>Last updated: {formatDate(editingArticle.updated_at)}</p>
                </div>
              )}
            </TabsContent>
          </Tabs>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setArticleDialogOpen(false)}
            >
              Cancel
            </Button>
            <Button onClick={saveArticle} data-testid="save-article-btn">
              {editingArticle ? "Update" : "Publish"} Article
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ArticlesTab;
